/* eslint-disable max-len */
/*
  This code is the intellectual property of Bulb Inc. and other software publishers;
  it may not be altered, copied or disclosed without prior written approval.
  Copyright 2022, Bulb Inc. For more information, please email contact@hellobulb.com.
*/
import {
  Component, forwardRef, Inject, Input, OnInit,
} from '@angular/core';
import {
  ControlValueAccessor, UntypedFormControl, NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { SvgIconRegistry } from '@ngneat/svg-icon';
import { TranslocoService, TRANSLOCO_SCOPE } from '@jsverse/transloco';

import { faMagicIcon } from 'IconLibrary/fontawesome/regular/magic';
import { faCropAltIcon } from 'IconLibrary/fontawesome/regular/crop-alt';
import { faTrashAltIcon } from 'IconLibrary/fontawesome/regular/trash-alt';
import { faTimesCircleIcon } from 'IconLibrary/fontawesome/regular/times-circle';
import { faSaveIcon } from 'IconLibrary/fontawesome/regular/save';
import { fasImagePolaroidIcon } from 'IconLibrary/fontawesome/solid/image-polaroid';

import {
  MenuItem, TypeOptions, MenuListType, ColorOptions,
} from '../../menus/types';
import { CommandsFromAjs } from '../types';

@Component({
  selector: 'bulb-header-image-controls',
  templateUrl: './header-image-controls.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HeaderImageControlsComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class HeaderImageControlsComponent implements OnInit, ControlValueAccessor {

  @Input() hasCoverImage: boolean;

  @Input() commandsFromAjs: CommandsFromAjs;

  onChange = Function;

  onTouched = () => {};

  formControl: UntypedFormControl = new UntypedFormControl('');

  public editControls: MenuItem[];

  public noImageControls: MenuItem[];

  constructor(
    private svgRegistry: SvgIconRegistry,
    private transloco: TranslocoService,
    @Inject(TRANSLOCO_SCOPE) private scope,
  ) { }

  ngOnInit(): void {
    this.createMenus(this.commandsFromAjs);
    this.translateMenuLabels();
    this.registerMenuIcons();

    this.formControl.valueChanges.subscribe((value) => {
      this.onChange(value);
    });
  }

  writeValue(value: any): void {
    this.formControl.patchValue(value);
    this.onChange(value);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  private createMenus(commandsFromAjs: CommandsFromAjs): void {
    this.editControls = [
      {
        label: 'Change Cover Image Theme',
        icon: 'magic',
        type: TypeOptions.MENU_PANEL,
        model: 'headerEffectType',
        value: 'ZOOM_OUT',
        menuPanel: {
          listType: MenuListType.RADIO,
          menuItems: [
            {
              label: 'No Effect',
              icon: 'magic',
              model: 'headerEffectType',
              value: 'NO_EFFECT',
              closeMenuOnClick: true,
            },
            {
              label: 'Parallax',
              icon: 'magic',
              model: 'headerEffectType',
              value: 'PARALLAX',
              closeMenuOnClick: true,
            },
            {
              label: 'Zoom Out',
              icon: 'magic',
              model: 'headerEffectType',
              value: 'ZOOM_OUT',
              closeMenuOnClick: true,
            },
          ],
        },
      },
      {
        label: 'Crop Cover Image',
        icon: 'crop-alt',
        command: () => commandsFromAjs.editCoverImage(),
      },
      {
        label: 'Remove Cover Image',
        icon: 'trash-alt',
        command: () => commandsFromAjs.deleteCoverImage(),
        color: ColorOptions.ALERT,
      },
    ];

    this.noImageControls = [
      {
        label: 'Add Cover Image',
        icon: 'image-polaroid',
        showLabel: true,
        value: 'No_Image',
        command: () => commandsFromAjs.openImagePicker(),
      },
    ];
  }

  private translateMenuLabels(): void {
    this.transloco.selectTranslateObject('coverImage.controls', {}, this.scope)
      .subscribe((result) => {
        this.editControls[0].label = result.imageEffect.label;
        this.editControls[0].menuPanel.menuItems[0].label = result.imageEffect.options.noEffect;
        this.editControls[0].menuPanel.menuItems[1].label = result.imageEffect.options.parallax;
        this.editControls[0].menuPanel.menuItems[2].label = result.imageEffect.options.zoomOut;
        this.editControls[1].label = result.crop.label;
        this.editControls[2].label = result.remove.label;

        this.noImageControls[0].label = result.add.label;
      });
  }

  private registerMenuIcons(): void {
    this.svgRegistry.register([
      faMagicIcon,
      faCropAltIcon,
      faTrashAltIcon,
      faTimesCircleIcon,
      faSaveIcon,
      fasImagePolaroidIcon,
    ]);
  }

}
